/**
 * Native secure storage (Android Keystore / iOS Keychain) — the ONLY module in
 * the app that imports `@aparajita/capacitor-secure-storage`
 * (`specs/offline-attendance-v1/implementation-plan.md`, decision D5, task
 * FE-3). Its one consumer today is `services/storage/offline-keys.ts`, which
 * keeps the offline-attendance data key here on a native shell.
 *
 * Same contract as every other `services/device/` module, plus three rules of
 * its own:
 *   - NATIVE ONLY. The plugin also registers a web implementation — and that
 *     one is plaintext `localStorage`. So {@link isSupported} requires a native
 *     platform AND the native plugin, and nothing here ever calls the plugin
 *     on the web: a secret must never land in `localStorage` because a
 *     platform check was skipped.
 *   - NATIVE CODE, NOT JUST JS. Unlike the official `@capacitor/*` plugins
 *     (which register a web loader only), this plugin's JS registers web, iOS
 *     AND Android loaders — so `Capacitor.isPluginAvailable('SecureStorage')`
 *     is true in every native shell, even one whose app was built without the
 *     plugin's native code (an `android/` project not re-synced after the
 *     dependency arrived; it is git-ignored and regenerated by hand). A call
 *     there does not fail: the JS wrapper resolves each internal method back to
 *     itself and chains promises forever — the call never settles and starves
 *     the JS thread (timers never fire again; verified against Capacitor 8.5.2
 *     and plugin 8.0.1). {@link isSupported} therefore also requires the
 *     bridge's `PluginHeaders` entry for the plugin, listing every native
 *     method the calls below go through.
 *   - NEVER THROWS. Reads answer `{ ok: false }`, writes and removals answer
 *     `false`; the caller decides what an unreadable store means (for the data
 *     key: "unavailable", never "lost" — a key must not be recreated because
 *     of a transient keystore error). ⚠ One transient case this facade CANNOT
 *     tell apart: on iOS the plugin answers a LOCKED keychain (a
 *     `whenUnlockedThisDeviceOnly` item while the phone is locked) as an absent
 *     value, `{ ok: true, value: null }` — so with a marker present it reads as
 *     "lost". The orchestrator re-reads the key on resume; the P6 device gate
 *     must confirm no recovery ever starts from that state.
 *
 * Values are strings only (`set()` JSON-encodes them, `get()` decodes; date
 * conversion is off both ways). Keys use the plugin's default prefix, and iOS
 * iCloud keychain sync stays off (the plugin default). Nothing here logs a
 * value — only an operation name and an error code.
 *
 * DEVICE-BOUND (plan amendment C17). Every write uses the keychain
 * accessibility `whenUnlockedThisDeviceOnly`, never the plugin's default
 * `whenUnlocked`: an item with the default MIGRATES to a new iPhone with an
 * encrypted backup — and so would the offline data key, next to an IndexedDB
 * (installation id, device sequence) restored with it, silently cloning the
 * installation onto a second device. A device-only item stays behind, so a
 * restored device reads its data key as lost (`blocked/key_unavailable`,
 * recovery belongs to the P6 device gate). Android ignores the option: its
 * Keystore keys never leave the device anyway.
 */
import { Capacitor } from '@capacitor/core';
import { KeychainAccess, SecureStorage } from '@aparajita/capacitor-secure-storage';
import { IS_DEV } from '@/utils/env';

/** The plugin's registered name, as `Capacitor.isPluginAvailable()` knows it. */
const PLUGIN_NAME = 'SecureStorage';

/**
 * The native methods `SecureStorage.get` / `set` / `remove` reach through the
 * plugin's JS (`internalGetItem` / `internalSetItem` / `internalRemoveItem`).
 * One missing from the native build is enough to hang its call (see header).
 */
const REQUIRED_NATIVE_METHODS: readonly string[] = ['internalGetItem', 'internalSetItem', 'internalRemoveItem'];

/** What a read can answer: a string, `null` for "no such key", or "could not read". */
export type SecureStringRead = { ok: true; value: string | null } | { ok: false };

/**
 * True only inside a native shell whose app carries the secure-storage plugin's
 * NATIVE code — every method this facade calls — not merely its JS.
 */
export function isSupported(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable(PLUGIN_NAME) && hasNativeImplementation();
}

/**
 * Does the native bridge list the plugin with every method we call? Read from
 * `Capacitor.PluginHeaders` — the manifest the Android/iOS bridge injects
 * before the app's JS runs, and what `isPluginAvailable()` itself falls back
 * to. Not in Capacitor's public typings, hence the narrowing; absent on the
 * web and in a shell without native plugins.
 */
function hasNativeImplementation(): boolean {
  const headers: unknown = Reflect.get(Capacitor, 'PluginHeaders');
  if (!Array.isArray(headers)) return false;
  const header: unknown = headers.find((entry: unknown) => isRecord(entry) && entry.name === PLUGIN_NAME);
  if (!isRecord(header) || !Array.isArray(header.methods)) return false;
  const methods = new Set<string>();
  for (const method of header.methods as unknown[]) {
    if (isRecord(method) && typeof method.name === 'string') methods.add(method.name);
  }
  return REQUIRED_NATIVE_METHODS.every((name) => methods.has(name));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Read the string stored under `key`. `{ ok: true, value: null }` means the key
 * does not exist; `{ ok: false }` means the store could not be read — the
 * plugin is unsupported here, the OS refused, or the stored value is not a
 * string (never reported as "absent").
 */
export async function getString(key: string): Promise<SecureStringRead> {
  if (!isSupported()) return { ok: false };
  try {
    const value: unknown = await SecureStorage.get(key, false);
    if (value === null) return { ok: true, value: null };
    if (typeof value === 'string') return { ok: true, value };
    warn('get', 'the stored value is not a string');
    return { ok: false };
  } catch (err) {
    // An OS keystore error, a locked keychain, or data the plugin cannot decode
    // (StorageError invalidData) — reported as unreadable, never thrown.
    warn('get', err);
    return { ok: false };
  }
}

/**
 * Store `value` under `key`, readable only while the device is unlocked and
 * never migrated by a backup (see the header). `true` once the plugin confirms
 * the write.
 */
export async function setString(key: string, value: string): Promise<boolean> {
  if (!isSupported()) return false;
  try {
    // set(key, data, convertDate, sync, access): no date conversion; `sync`
    // undefined keeps the plugin's default (iCloud sync off); device-only access.
    await SecureStorage.set(key, value, false, undefined, KeychainAccess.whenUnlockedThisDeviceOnly);
    return true;
  } catch (err) {
    // The keystore refused the write (OS error, missing key name) — report, never throw.
    warn('set', err);
    return false;
  }
}

/** Remove `key`. `true` when the plugin completed the removal (whether or not the key existed). */
export async function remove(key: string): Promise<boolean> {
  if (!isSupported()) return false;
  try {
    await SecureStorage.remove(key);
    return true;
  } catch (err) {
    // The keystore refused the removal — report, never throw.
    warn('remove', err);
    return false;
  }
}

/** Grouped export, mirroring the other device services. */
export const secureStorage = {
  isSupported,
  getString,
  setString,
  remove,
};

/** Dev-only diagnostics: the operation and an error code or name — never a stored value. */
function warn(operation: string, err: unknown): void {
  if (!IS_DEV) return;
  console.warn(`[secure-storage] ${operation}`, describe(err));
}

function describe(err: unknown): string {
  if (typeof err === 'string') return err;
  if (typeof err === 'object' && err !== null) {
    const code: unknown = Reflect.get(err, 'code');
    if (typeof code === 'string' && code.length > 0) return code;
    const name: unknown = Reflect.get(err, 'name');
    if (typeof name === 'string' && name.length > 0) return name;
  }
  return 'unknown error';
}
